// src/context/AuthContext.tsx
// Provides auth state globally so any component can read user/role
// without prop drilling.

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { LoginRequest, LoginResponse, AuthError, AuthState } from '../types/auth.types';
import { authService } from '../services/auth.service';

interface AuthContextValue extends AuthState {
  login: (credentials: LoginRequest) => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
  hasRole: (role: string) => boolean;
  isAdmin: boolean;
  isOperator: boolean;
  isViewer: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: false,
    error: null,
  });

  // Restore session on mount
  useEffect(() => {
    const stored = authService.getStoredUser();
    if (stored && authService.isSessionActive()) {
      setState({ isAuthenticated: true, user: stored, isLoading: false, error: null });
    }
  }, []);

  const login = useCallback(async (credentials: LoginRequest): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await authService.login(credentials);
      authService.saveSession(response);
      setState({ isAuthenticated: true, user: response, isLoading: false, error: null });
      return true;
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false, error: error as AuthError }));
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    authService.clearSession();
    setState({ isAuthenticated: false, user: null, isLoading: false, error: null });
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const hasRole = useCallback((role: string): boolean => {
    if (!state.user) return false;
    return state.user.roles.split(',').map(r => r.trim()).includes(role);
  }, [state.user]);

  const roles = state.user?.roles.split(',').map(r => r.trim()) ?? [];

  return (
    <AuthContext.Provider value={{
      ...state,
      login,
      logout,
      clearError,
      hasRole,
      isAdmin:    roles.includes('ADMIN'),
      isOperator: roles.includes('OPERATOR'),
      isViewer:   roles.includes('VIEWER'),
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used inside <AuthProvider>');
  return ctx;
};
